import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Portal — renders children as a direct child of `document.body`.
 *
 * WHY THIS EXISTS:
 * The `.glass-header` (and other future glass/blur containers) use
 * `backdrop-filter: blur(...)`, which per CSS spec turns that element into a
 * containing block for its fixed-position descendants. If a `<div className="fixed inset-0">`
 * lives inside such a container, `bottom: 0` / `right: 0` resolve against the
 * container's box instead of the viewport — so full-screen overlays get clamped
 * to a few pixels tall and appear invisible.
 *
 * Portaling to `document.body` sidesteps the trap: the overlay is positioned
 * against the viewport as intended.
 *
 * Also handy for z-index sanity — the overlay always sits above app content,
 * regardless of where it was declared in the tree.
 *
 * BROWSER SUPPORT:
 * - React `createPortal` is supported everywhere React 18/19 is: Chromium
 *   (Chrome, Edge, Brave, Opera), Firefox, and WebKit (Safari desktop + iOS ≥ 13).
 * - We guard against SSR / early-render by only mounting once `document` is
 *   available. Under CRA this is effectively always, but the guard is cheap
 *   insurance if the app is ever rendered outside a browser.
 * - iOS Safari sometimes fails to receive taps on elements with a `pointer-events`
 *   inherited from a transformed ancestor. Portaling breaks that chain, so
 *   overlay clicks work correctly on iPhones too.
 *
 * Usage:
 *   <Portal>
 *     <div className="fixed inset-0 bg-black/50 z-[60]">…modal…</div>
 *   </Portal>
 *
 * Optional props:
 *   `to`        — a specific DOM node (default: document.body)
 *   `disabled`  — render inline instead of portaling (for tests / edge cases)
 */
export default function Portal({ children, to, disabled = false }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (disabled) return children;
  if (!mounted || typeof document === "undefined") return null;

  return createPortal(children, to || document.body);
}
